import React from 'react';
import {
  SvgMapInteractionsEventMap,
  UseSvgMapInteractionsReturn
} from '@gettactic/helpers/src/map/svg/useSvgMapInteractions';
import { useBooleanState } from '../../../../../helpers';
import { ZoomControls } from '../../ZoomControls';

interface SvgMiniMapProps {
  width?: number;
  height?: number;
  mapInteractor: UseSvgMapInteractionsReturn;
  disabled?: boolean;
}

const classes = {
  ZoomControlsWrapper:
    'flex rounded-md items-center absolute top-14 right-10 bg-background-primary shadow-sm transition-opacity duration-300 ease-in-out select-none'
};

export const SvgMiniMap: React.FC<SvgMiniMapProps> = ({
  width: defaultWidth = 200,
  height: defaultHeight = 150,
  mapInteractor,
  disabled = false
}) => {
  const viewPortRef = React.useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = React.useState({
    left: 0,
    top: 0,
    width: 50,
    height: 50
  });

  const isMapReady = useBooleanState();

  const [scaleFactor, setScaleFactor] = React.useState(1);
  const [mapScale, setMapScale] = React.useState(1);
  const [svgContent, setSvgContent] = React.useState<string | null>(null);
  const [svgBBox, setSvgBBox] = React.useState<DOMRect | null>(null);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!isMapReady.state) {
      setSvgContent('');
      return;
    }

    const container = mapInteractor.mapRef.current?.closest(
      '.item-container'
    ) as HTMLElement;
    const svg = container?.querySelector<SVGSVGElement>('.injected-svg');

    if (!container || !svg) return;

    const svgBBox = svg.getBBox();
    setSvgBBox(svgBBox);

    const scaleX = defaultWidth / svgBBox.width;
    const scaleY = defaultHeight / svgBBox.height;
    const scale = Math.min(scaleX, scaleY);

    setScaleFactor(scale);

    // Centering offset
    const offsetX = (defaultWidth - svgBBox.width * scale) / 2;
    const offsetY = (defaultHeight - svgBBox.height * scale) / 2;
    setOffset({ x: offsetX, y: offsetY });

    const clonedSvg = svg.cloneNode(true) as SVGSVGElement;
    clonedSvg.removeAttribute('style');
    clonedSvg.setAttribute('width', `${svgBBox.width * scale}`);
    clonedSvg.setAttribute('height', `${svgBBox.height * scale}`);
    clonedSvg.setAttribute(
      'viewBox',
      `${svgBBox.x} ${svgBBox.y} ${svgBBox.width} ${svgBBox.height}`
    );
    clonedSvg.setAttribute(
      'style',
      `position: absolute; left: ${offsetX}px; top: ${offsetY}px; display: block;`
    );
    setSvgContent(clonedSvg.outerHTML);
  }, [
    mapInteractor,
    defaultWidth,
    defaultHeight,
    mapInteractor.mapRef,
    isMapReady.state
  ]);

  React.useEffect(() => {
    const onTransform = (
      transform: SvgMapInteractionsEventMap['transform']
    ) => {
      const container = mapInteractor.mapRef.current?.closest(
        '.item-container'
      ) as HTMLElement;
      const svg = mapInteractor.svgRef.current;

      if (!container || !svg || !svgBBox) return;
      setMapScale(transform.scale);
      const containerRect = container.getBoundingClientRect();

      const visibleWidth = containerRect.width / transform.scale;
      const visibleHeight = containerRect.height / transform.scale;
      const visibleX = -transform.tx / transform.scale;
      const visibleY = -transform.ty / transform.scale;

      setViewport({
        left: (visibleX - svgBBox.x) * scaleFactor + offset.x,
        top: (visibleY - svgBBox.y) * scaleFactor + offset.y,
        width: visibleWidth * scaleFactor,
        height: visibleHeight * scaleFactor
      });
    };

    const onMapReady = () => {
      isMapReady.true();
    };

    const onMapClosed = () => {
      isMapReady.false();
    };

    if (isMapReady.state) {
      mapInteractor.subscribe('transform', onTransform);
    }
    mapInteractor.subscribe('ready', onMapReady);
    mapInteractor.subscribe('closed', onMapClosed);
    return () => {
      mapInteractor.unsubscribe('transform', onTransform);
      mapInteractor.unsubscribe('ready', onMapReady);
      mapInteractor.unsubscribe('closed', onMapClosed);
    };
  }, [mapInteractor, scaleFactor, svgBBox, offset, isMapReady.state]);

  const handleMinimapMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (!svgBBox || !mapInteractor) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const container = mapInteractor.mapRef.current?.closest(
      '.item-container'
    ) as HTMLElement;

    if (!container) return;

    const scale = mapScale ?? 1;
    const containerRect = container.getBoundingClientRect();

    const moveTo = (clientX: number, clientY: number) => {
      const x = clientX - rect.left - offset.x;
      const y = clientY - rect.top - offset.y;

      const mapPointX = svgBBox.x + x / scaleFactor;
      const mapPointY = svgBBox.y + y / scaleFactor;

      const tx = -mapPointX * scale + containerRect.width / 2;
      const ty = -mapPointY * scale + containerRect.height / 2;

      mapInteractor.setTransform(tx, ty, scale);
    };

    moveTo(event.clientX, event.clientY);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveTo(moveEvent.clientX, moveEvent.clientY);
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <>
      <div
        className={classes.ZoomControlsWrapper}
        style={{ opacity: isMapReady.state ? 1 : 0 }}
      >
        <ZoomControls
          scale={mapScale || 1}
          onZoomIn={mapInteractor.zoomIn}
          onZoomOut={mapInteractor.zoomOut}
          onCenter={mapInteractor.centerAndFitMap}
          disabled={disabled}
        />
      </div>
      <div
        className={'transition-opacity duration-300 ease-in-out hidden md:block'}
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          width: `${defaultWidth}px`,
          height: `${defaultHeight}px`,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid rgba(0, 0, 0, 0.2)',
          borderRadius: '8px',
          overflow: 'hidden',
          pointerEvents: 'auto',
          opacity: isMapReady.state ? 1 : 0
        }}
      >
        {svgContent && (
          <div
            className="absolute inset-0"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        )}
        <div
          ref={viewPortRef}
          style={{
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid black',
            pointerEvents: 'none',
            ...viewport
          }}
        />
        <div
          className={'absolute inset-0'}
          onMouseDown={!disabled ? handleMinimapMouseDown : undefined}
        />
      </div>
    </>
  );
};
